import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { RevealText, FadeIn } from "../components/RevealText";
import { SUPPORT } from "../lib/api";

const FAQS = [
  {
    q: "What is saveu?",
    a: "saveu is a campus nightlife app that shows how busy bars and restaurants near your school are in real time, unlocks student-only deals, and rewards you with points and a leaderboard every time you go out.",
  },
  {
    q: "How do the live busyness meters work?",
    a: "saveu aggregates real-time activity signals to estimate how packed each venue is right now — from quiet to slammed — so you can pick the right spot before you leave.",
  },
  {
    q: "How do I earn points?",
    a: "You earn points automatically by checking in at venues and redeeming deals through the app. The more you explore campus nightlife, the more points you stack.",
  },
  {
    q: "What is the leaderboard?",
    a: "The leaderboard ranks you against friends and everyone at your campus based on the points you earn. Climb to the top to unlock perks and campus bragging rights.",
  },
  {
    q: "Is saveu free to use?",
    a: "Yes. saveu is free to download and use. The deals and rewards are there to save you money when you go out.",
  },
  {
    q: "Which campuses does saveu support?",
    a: "We're expanding to new campuses regularly. If your school or a local venue isn't listed yet, email us and we'll prioritize adding it.",
  },
  {
    q: "How do I delete my account or data?",
    a: `You can request account and data deletion any time by emailing ${SUPPORT.email}. See our Privacy Policy for details on how we handle your data.`,
  },
  {
    q: "How do I report a bug or contact support?",
    a: "Head to our Support page to send a message directly to the team, or email us. We read every message and reply as fast as we can.",
  },
];

export default function FAQPage() {
  return (
    <div data-testid="faq-page" className="min-h-screen">
      <section className="px-6 lg:px-10 pt-40 pb-16">
        <div className="mx-auto max-w-4xl">
          <p className="font-body uppercase tracking-[0.4em] text-xs text-brand-dark mb-8">
            Frequently Asked
          </p>
          <RevealText
            as="h1"
            lines={["Questions,", "answered."]}
            className="font-display font-black uppercase tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            delay={0.15}
          />
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-xl text-lg text-ink-soft leading-relaxed">
              Everything you need to know about saveu. Can't find your answer?{" "}
              <Link to="/support" className="text-brand font-semibold hover:underline">
                Reach the team
              </Link>
              .
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-32">
        <div className="mx-auto max-w-4xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-ink/10"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-display font-bold text-lg md:text-xl tracking-tight text-ink hover:text-brand py-7 hover:no-underline"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${i}`}
                  className="text-ink-soft text-base leading-relaxed pb-7 max-w-2xl"
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <FadeIn>
            <div className="mt-20 rounded-3xl bg-white border border-ink/10 p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h3 className="font-display font-bold text-2xl tracking-tight text-ink">
                  Still stuck?
                </h3>
                <p className="mt-2 text-ink-soft">
                  Our support team usually replies within a day.
                </p>
              </div>
              <Link
                to="/support"
                data-testid="faq-contact-btn"
                className="glow-ring bg-brand text-white font-body font-semibold uppercase tracking-wider px-8 py-4 rounded-full hover:-translate-y-1 transition-transform flex items-center gap-2 whitespace-nowrap"
              >
                Contact Support <ArrowUpRight size={18} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
